"use client";

/**
 * Subtle haptic feedback via the Web Vibration API. Best-effort and progressive: it fires on Android browsers
 * that support `navigator.vibrate`, and silently no-ops where it isn't supported (notably iOS Safari, which
 * disables it) — on those the visual press/transition feedback carries the "native" feel instead.
 *
 * Keep patterns SHORT so they read as a tap, never a buzz. Use sparingly — on meaningful interactions
 * (selecting from a sheet, toggling, confirming) — not on every tap.
 */
export type HapticPattern = "light" | "selection" | "medium" | "success" | "warning";

const PATTERNS: Record<HapticPattern, number | number[]> = {
  light: 8,
  selection: 8,
  medium: 14,
  success: [10, 30, 12],
  warning: [14, 28, 14],
};

export function triggerHaptic(pattern: HapticPattern = "light"): void {
  if (typeof window === "undefined") return;
  const nav = window.navigator;
  if (!nav || typeof nav.vibrate !== "function") return;
  try {
    // Don't buzz for users who asked for reduced motion.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return;
    nav.vibrate(PATTERNS[pattern]);
  } catch {
    // Vibration is best-effort; ignore failures (blocked permission, etc.).
  }
}
